import { ICloneable } from '../../contracts/cloneable.js';
import { IDataComparable } from '../../contracts/data-comparable.js';
import { isNotNullOrUndefined, isNullOrUndefined } from '../../utility/null-safety.js';
import { SerializationException } from '../../exceptions/serialization-exception.js';
import { Country } from '../serializable/country.js';
import {
  getPhoneCountryFromCountry,
  getPhoneCountryFromPrefix,
  getPrefixFromPhoneCountry,
  PhoneCountry,
} from './phone-country.js';
import { isNotNullOrUndefinedOrEmpty, isNullOrUndefinedOrEmpty } from '../../utility/string.js';

export class PhoneNumber implements ICloneable, IDataComparable {
  static readonly encodedPhoneNumberMatcher: RegExp = new RegExp('^\\((\\+\\d+)\\)(\\d+)$');

  constructor(
    public readonly country: PhoneCountry,
    public readonly number: string,
  ) {}

  static create(args: {
    country: PhoneCountry,
    number: string,
  }): PhoneNumber {
    return new PhoneNumber(args.country, args.number);
  }

  static ofCountry(country: Country): PhoneNumber | null {
    const phoneCountry = getPhoneCountryFromCountry(country);
    return phoneCountry ? this.ofPhoneCountry(phoneCountry) : null;
  }

  static ofPhoneCountry(country: PhoneCountry): PhoneNumber  {
    return new PhoneNumber(country, '');
  }

  static decode(encodedNumber: string | null): PhoneNumber | null {
    if(isNullOrUndefinedOrEmpty(encodedNumber)) return null;
    
    const matches = encodedNumber.match(this.encodedPhoneNumberMatcher);
    if(isNullOrUndefined(matches) || matches.length <= 2) return null;

    const country = getPhoneCountryFromPrefix(matches[1]);
    if(isNullOrUndefined(country)) return null;

    const number = matches[2];
    if(isNotNullOrUndefinedOrEmpty(number)) {
      return this.create({ country, number });
    }

    return this.ofPhoneCountry(country);
  }

  static mustDecode(encodedNumber: string | null): PhoneNumber {
    const phoneNumber = this.decode(encodedNumber);
    if(isNotNullOrUndefined(phoneNumber)) {
      return phoneNumber;
    }

    throw new SerializationException(`Failed to deserialize encoded phone number: ${encodedNumber}`);
  }

  get asFormattedString(): string {
    return getPrefixFromPhoneCountry(this.country) + this.number;
  }

  encode(): string {
    return `(${getPrefixFromPhoneCountry(this.country)})${this.number}`;
  }

  toISOE164(): string {
    return `+${getPrefixFromPhoneCountry(this.country)}${this.number}`;
  }

  toPrefixAndNumber(): string {
    return `${getPrefixFromPhoneCountry(this.country)}${this.number}`;
  }

  clone(): this {
    return new PhoneNumber(
        this.country,
        this.number,
    ) as this;
  }

  isSame(other: PhoneNumber): boolean {
    if(other === this) {
      return true;
    }

    return other.country === this.country &&
      other.number === this.number;
  }

  invalidate(): PhoneNumber {
    return new PhoneNumber(this.country, this.number.replace(/\d/g, '0'));
  }
}
